import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { getErrorMessageForRequiredFields, removeNonDigits } from 'prequalificationV2/utils/stringUtils';
import { isValidEmail, validatePhoneNumber } from 'prequalificationV2/utils/validationUtils';
import { checkBirthDate } from 'prequalificationV2/utils/dateUtils';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { PhoneLineType } from 'api/PreQualification/preQualificationApi.enums';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { INVALID_PHONE_LINE_TYPE } from 'prequalificationV2/pages/PreQualification/steps/LoanInformation/loanInformation.constants';
import { setPrequalificationPortal } from 'prequalificationV2/actions/prequalificationPortal';
import { BorrowerIdentityFormType } from 'prequalificationV2/pages/PreQualification/steps/BorrowerIdentity/borrowerIdentity.types';

export const useBorrowerIdentityForm = (submitForm: (value: BorrowerIdentityFormType) => Promise<void>) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const borrowerState = useAppSelector((state) => state.borrower);
  const { isNeedToClearLoanInformation, isBorrowerAuthorized } = useAppSelector(
    (state) => state.prequalificationPortal,
  );
  const {
    register,
    unregister,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
    watch,
    clearErrors,
    handleSubmit,
    reset,
    setError,
    control,
  } = useForm<BorrowerIdentityFormType>({
    defaultValues: {
      [LoanInformationVariables.BorrowerCellPhone]: borrowerState.phone,
      [LoanInformationVariables.BorrowerDateOfBirth]: borrowerState.dateOfBirth,
      [LoanInformationVariables.BorrowerEmail]: borrowerState.email,
      [LoanInformationVariables.FullConsentCheckbox]: false,
    },
  });

  const watcher = watch();

  useEffect(() => {
    register(LoanInformationVariables.BorrowerCellPhone, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerCellPhone),
      validate: (value) => {
        return validatePhoneNumber(value);
      },
    });
    register(LoanInformationVariables.BorrowerDateOfBirth, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerDateOfBirth),
      validate: (value) => checkBirthDate(value) || 'Please enter a valid date',
    });
    register(LoanInformationVariables.BorrowerEmail, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerEmail),
      validate: (value) => {
        return isValidEmail(value) || 'Please enter a valid email address';
      },
    });
    register(LoanInformationVariables.FullConsentCheckbox, {
      required: false,
      validate: () => true,
    });
  }, [register, unregister, watcher, isBorrowerAuthorized]);

  useEffect(() => {
    if (isNeedToClearLoanInformation) {
      reset({});
      dispatch(setPrequalificationPortal({ isNeedToClearLoanInformation: false }));
    }
  }, [dispatch, reset, isNeedToClearLoanInformation]);

  const checkBorrowersPhonesLineType = async () => {
    try {
      const phoneLineTypes = await preQualificationApi.getPhoneNumberLineType({
        borrowerPhoneNumber: removeNonDigits(watcher[LoanInformationVariables.BorrowerCellPhone]),
      });

      let phoneVerificationPassed = true;

      if (phoneLineTypes.borrowerLineType !== PhoneLineType.Mobile) {
        phoneVerificationPassed = false;

        setError(LoanInformationVariables.BorrowerCellPhone, {
          type: 'manual',
          message: INVALID_PHONE_LINE_TYPE,
        });
      }

      return phoneVerificationPassed;
    } catch (error) {
      // if prove return error during checking phone line type we don't stop the flow
      return true;
    }
  };

  const handleSubmitLoanForm = handleSubmit(async () => {
    setIsLoading(true);

    const result = await trigger();
    const isValidPhoneLineType = await checkBorrowersPhonesLineType();

    if (isValidPhoneLineType && result) {
      await submitForm(watcher);
    }

    setIsLoading(false);
  });

  return {
    register: register,
    unregister: unregister,
    errors: errors,
    isSubmitted: isSubmitted,
    setValue: setValue,
    trigger: trigger,
    watcher: watcher,
    handleSubmitLoanForm: handleSubmitLoanForm,
    reset: reset,
    clearErrors: clearErrors,
    checkBorrowersPhonesLineType: checkBorrowersPhonesLineType,
    isLoading: isLoading,
    control: control,
  };
};
